import React, { useState, useEffect } from "react";
import { useLocation, useParams, useNavigate } from "react-router-dom";
import { Form, Row, Col, Button } from "react-bootstrap";
import { FaPencil } from "react-icons/fa6";
import axios from "axios";
import Swal from "sweetalert2";
import { Country, State } from "country-state-city";

const Details = () => {
  // Get accountId from URL params and accountData passed via navigation state
  const location = useLocation();
  const { accountId } = useParams();
  const navigate = useNavigate();
  const { accountData = {} } = location.state || {};

  // List of all countries for dropdown options
  const countries = Country.getAllCountries();

  // Controls visibility of the collapsible shipping address section
  const [showAddress, setShowAddress] = useState(false);

  // Determines if form fields are editable (true = editing mode, false = view mode)
  const [isEditing, setIsEditing] = useState(true);

  // ISO codes required by country-state-city library for dynamic state loading
  const [selectedCountryIso, setSelectedCountryIso] = useState("");
  const [selectedShippingCountryIso, setSelectedShippingCountryIso] = useState("");
  const [selectedStateIso, setSelectedStateIso] = useState("");
  const [selectedShippingStateIso, setSelectedShippingStateIso] = useState("");

  // Main form data – initialized from accountData passed from parent component
  const [formData, setFormData] = useState({
    account_name: accountData.account_name || "",
    address: accountData.address || "",
    address2: accountData.address2 || "",
    country: accountData.country || "",
    state: accountData.state || "",
    city: accountData.city || "",
    zip: accountData.zip || "",
    telephone: accountData.telephone || "",
    telephone2: accountData.telephone2 || "",
    fax: accountData.fax || "",
    email: accountData.email || "",
    website: accountData.website || "",
    time_zone: accountData.time_zone || "",
    status: accountData.status || "",
    account_type: accountData.account_type || "",
    ddxEnable: accountData.ddxEnable || false,
    quick_code: accountData.quick_code || "",
    deny_ddx_case_creation: accountData.deny_ddx_case_creation || false,
    shipping_address: accountData.shipping_address || "",
    shipping_address2: accountData.shipping_address2 || "",
    shipping_country: accountData.shipping_country || "",
    shipping_state: accountData.shipping_state || "",
    shipping_city: accountData.shipping_city || "",
    shipping_zip: accountData.shipping_zip || "",
    shipping_time_zone: accountData.shipping_time_zone || "",
  });

  // Account operating hours – default values
  const [accountHours, setAccountHours] = useState([
    { day: "Monday", status: "9:00 AM - 5:00 PM" },
    { day: "Tuesday", status: "9:00 AM - 5:00 PM" },
    { day: "Wednesday", status: "9:00 AM - 5:00 PM" },
    { day: "Thursday", status: "9:00 AM - 5:00 PM" },
    { day: "Friday", status: "9:00 AM - 5:00 PM" },
    { day: "Saturday", status: "Closed" },
    { day: "Sunday", status: "Closed" },
  ]);

  // Populate country and state dropdowns when component mounts or accountData changes
  useEffect(() => {
    if (accountData.country) {
      const country = countries.find((c) => c.name === accountData.country);
      if (country) {
        setSelectedCountryIso(country.isoCode);
        if (accountData.state) {
          const stateList = State.getStatesOfCountry(country.isoCode);
          const state = stateList.find((s) => s.name === accountData.state);
          if (state) setSelectedStateIso(state.isoCode);
        }
      }
    }

    if (accountData.shipping_country) {
      const shippingCountry = countries.find((c) => c.name === accountData.shipping_country);
      if (shippingCountry) {
        setSelectedShippingCountryIso(shippingCountry.isoCode);
        if (accountData.shipping_state) {
          const shippingStateList = State.getStatesOfCountry(shippingCountry.isoCode);
          const shippingState = shippingStateList.find((s) => s.name === accountData.shipping_state);
          if (shippingState) setSelectedShippingStateIso(shippingState.isoCode);
        }
      }
    }
  }, [accountData, countries]);

  // Generic handler for updating form fields (text inputs & checkboxes)
  const handleInputChange = (field, value) => {
    setFormData({ ...formData, [field]: value });
  };

  // Update operating hours for a specific day
  const handleTimeChange = (index, newStatus) => {
    const updatedHours = [...accountHours];
    updatedHours[index].status = newStatus;
    setAccountHours(updatedHours);
  };

  // Handle change in main billing country
  const handleCountryChange = (e) => {
    const iso = e.target.value;
    const country = countries.find((c) => c.isoCode === iso);
    const countryName = country ? country.name : "";
    setSelectedCountryIso(iso);
    setSelectedStateIso("");
    setFormData({ ...formData, country: countryName, state: "" });
  };

  // Handle change in main billing state
  const handleStateChange = (e) => {
    const iso = e.target.value;
    const stateList = selectedCountryIso ? State.getStatesOfCountry(selectedCountryIso) : [];
    const state = stateList.find((s) => s.isoCode === iso);
    const stateName = state ? state.name : "";
    setSelectedStateIso(iso);
    setFormData({ ...formData, state: stateName });
  };

  // Handle change in shipping country
  const handleShippingCountryChange = (e) => {
    const iso = e.target.value;
    const country = countries.find((c) => c.isoCode === iso);
    const countryName = country ? country.name : "";
    setSelectedShippingCountryIso(iso);
    setSelectedShippingStateIso("");
    setFormData({ ...formData, shipping_country: countryName, shipping_state: "" });
  };

  // Handle change in shipping state
  const handleShippingStateChange = (e) => {
    const iso = e.target.value;
    const stateList = selectedShippingCountryIso ? State.getStatesOfCountry(selectedShippingCountryIso) : [];
    const state = stateList.find((s) => s.isoCode === iso);
    const stateName = state ? state.name : "";
    setSelectedShippingStateIso(iso);
    setFormData({ ...formData, shipping_state: stateName });
  };

  // Save all changes to backend and persist updated data for page refresh
  const handleSaveClick = async () => {
    try {
      // Show loading indicator
      Swal.fire({
        title: "Saving changes...",
        allowOutsideClick: false,
        didOpen: () => {
          Swal.showLoading();
        },
      });

      // Prepare payload
      const updateData = {
        ...formData,
        accountHours,
      };

      // Send PUT request to update account
      await axios.put(`https://hms.backend.hiragroupcloud.com/api/account/updateByAccountId/${accountId}`, updateData);

      // Merge updated values for persistence on refresh
      const updatedAccountData = {
        ...accountData,
        ...formData,
        accountHours,
      };

      // Show success message
      Swal.fire({
        icon: "success",
        title: "Success!",
        text: "Account updated successfully!",
        confirmButtonColor: "#004aad",
        timer: 3000,
        timerProgressBar: true,
      });

      // Switch to view mode
      setIsEditing(false);

      // Update route state so refresh loads latest saved data
      navigate(location.pathname, {
        replace: true,
        state: { accountData: updatedAccountData },
      });
    } catch (error) {
      // Show error message
      Swal.fire({
        icon: "error",
        title: "Update Failed",
        text: "Failed to update account. Please try again.",
        confirmButtonColor: "#d33",
      });
    }
  };

  // Enable editing mode when user clicks any field
  const handleFieldClick = () => {
    setIsEditing(true);
  };

  // Dynamic state lists based on selected countries
  const stateList = selectedCountryIso ? State.getStatesOfCountry(selectedCountryIso) : [];
  const shippingStateList = selectedShippingCountryIso
    ? State.getStatesOfCountry(selectedShippingCountryIso)
    : [];

  return (
    <div style={{ width: "99%", marginLeft: "10px" }}>
      <div className="row">
        {/* Page Header */}
        <h2 style={{ color: "#004aad" }}>Details</h2>

        {/* Account Name Field */}
        <div style={{ padding: "10px", backgroundColor: "#f4f4f4", borderTop: "3px solid #004aad" }}>
          <Row className="mb-3" style={{ alignItems: "center" }}>
            <Col xs={4}><b>Name <span style={{ color: "red" }}>*</span></b></Col>
            <Col xs={8}>
              <Form.Control
                type="text"
                value={formData.account_name}
                onChange={(e) => handleInputChange("account_name", e.target.value)}
                disabled={!isEditing}
                style={{ width: "200px", backgroundColor: "white" }}
                onClick={handleFieldClick}
              />
            </Col>
          </Row>
        </div>

        {/* Billing Address */}
        <div style={{ padding: "10px", backgroundColor: "#f2f2f2" }}>
          <Row className="mb-3" style={{ alignItems: "center" }}>
            <Col xs={4}><b>Address <span style={{ color: "red" }}>*</span></b></Col>
            <Col xs={8}>
              <Form.Control
                type="text"
                value={formData.address}
                onChange={(e) => handleInputChange("address", e.target.value)}
                disabled={!isEditing}
                style={{ width: "200px", backgroundColor: "white" }}
                onClick={handleFieldClick}
              />
            </Col>
          </Row>
        </div>

        <div style={{ padding: "10px", backgroundColor: "#f2f2f2" }}>
          <Row className="mb-3" style={{ alignItems: "center" }}>
            <Col xs={4}><b>Address 2</b></Col>
            <Col xs={8}>
              <Form.Control
                type="text"
                value={formData.address2}
                onChange={(e) => handleInputChange("address2", e.target.value)}
                disabled={!isEditing}
                style={{ width: "200px", backgroundColor: "white" }}
                onClick={handleFieldClick}
              />
            </Col>
          </Row>
        </div>

        {/* Billing Country */}
        <div style={{ padding: "10px", backgroundColor: "#f2f2f2" }}>
          <Row className="mb-3" style={{ alignItems: "center" }}>
            <Col xs={4}><b>Country <span style={{ color: "red" }}>*</span></b></Col>
            <Col xs={8}>
              <Form.Select
                value={selectedCountryIso}
                onChange={handleCountryChange}
                disabled={!isEditing}
                style={{ width: "200px", backgroundColor: "white" }}
                onClick={handleFieldClick}
              >
                <option value="">Select Country</option>
                {countries.map((country) => (
                  <option key={country.isoCode} value={country.isoCode}>
                    {country.name}
                  </option>
                ))}
              </Form.Select>
            </Col>
          </Row>
        </div>

        {/* Billing State */}
        <div style={{ padding: "10px", backgroundColor: "#f2f2f2" }}>
          <Row className="mb-3" style={{ alignItems: "center" }}>
            <Col xs={4}><b>State <span style={{ color: "red" }}>*</span></b></Col>
            <Col xs={8}>
              <Form.Select
                value={selectedStateIso}
                onChange={handleStateChange}
                disabled={!isEditing || !selectedCountryIso}
                style={{ width: "200px", backgroundColor: "white" }}
                onClick={handleFieldClick}
              >
                <option value="">Select State</option>
                {stateList.map((state) => (
                  <option key={state.isoCode} value={state.isoCode}>
                    {state.name}
                  </option>
                ))}
              </Form.Select>
            </Col>
          </Row>
        </div>

        {/* City */}
        <div style={{ padding: "10px", backgroundColor: "#f2f2f2" }}>
          <Row className="mb-3" style={{ alignItems: "center" }}>
            <Col xs={4}><b>City</b></Col>
            <Col xs={8}>
              <Form.Control
                type="text"
                value={formData.city}
                onChange={(e) => handleInputChange("city", e.target.value)}
                disabled={!isEditing}
                style={{ width: "200px", backgroundColor: "white" }}
                onClick={handleFieldClick}
              />
            </Col>
          </Row>
        </div>

        {/* Zip / Postal Code */}
        <div style={{ padding: "10px", backgroundColor: "#f2f2f2" }}>
          <Row className="mb-3" style={{ alignItems: "center" }}>
            <Col xs={4}><b>Zip / Postal Code <span style={{ color: "red" }}>*</span></b></Col>
            <Col xs={8}>
              <Form.Control
                type="text"
                value={formData.zip}
                onChange={(e) => handleInputChange("zip", e.target.value)}
                disabled={!isEditing}
                style={{ width: "200px", backgroundColor: "white" }}
                onClick={handleFieldClick}
              />
            </Col>
          </Row>
        </div>

        {/* Telephone */}
        <div style={{ padding: "10px", backgroundColor: "#f2f2f2" }}>
          <Row className="mb-3" style={{ alignItems: "center" }}>
            <Col xs={4}><b>Telephone <span style={{ color: "red" }}>*</span></b></Col>
            <Col xs={8}>
              <Form.Control
                type="text"
                value={formData.telephone}
                onChange={(e) => handleInputChange("telephone", e.target.value)}
                disabled={!isEditing}
                style={{ width: "200px", backgroundColor: "white" }}
                onClick={handleFieldClick}
              />
            </Col>
          </Row>
        </div>

        {/* Telephone 2 */}
        <div style={{ padding: "10px", backgroundColor: "#f2f2f2" }}>
          <Row className="mb-3" style={{ alignItems: "center" }}>
            <Col xs={4}><b>Telephone 2</b></Col>
            <Col xs={8}>
              <Form.Control
                type="text"
                value={formData.telephone2}
                onChange={(e) => handleInputChange("telephone2", e.target.value)}
                disabled={!isEditing}
                style={{ width: "200px", backgroundColor: "white" }}
                onClick={handleFieldClick}
              />
            </Col>
          </Row>
        </div>

        {/* Fax */}
        <div style={{ padding: "10px", backgroundColor: "#f2f2f2" }}>
          <Row className="mb-3" style={{ alignItems: "center" }}>
            <Col xs={4}><b>Fax</b></Col>
            <Col xs={8}>
              <Form.Control
                type="text"
                value={formData.fax}
                onChange={(e) => handleInputChange("fax", e.target.value)}
                disabled={!isEditing}
                style={{ width: "200px", backgroundColor: "white" }}
                onClick={handleFieldClick}
              />
            </Col>
          </Row>
        </div>

        {/* Email */}
        <div style={{ padding: "10px", backgroundColor: "#f2f2f2" }}>
          <Row className="mb-3" style={{ alignItems: "center" }}>
            <Col xs={4}><b>Email</b></Col>
            <Col xs={8}>
              <Form.Control
                type="email"
                value={formData.email}
                onChange={(e) => handleInputChange("email", e.target.value)}
                disabled={!isEditing}
                style={{ width: "200px", backgroundColor: "white" }}
                onClick={handleFieldClick}
              />
            </Col>
          </Row>
        </div>

        {/* Website */}
        <div style={{ padding: "10px", backgroundColor: "#f2f2f2" }}>
          <Row className="mb-3" style={{ alignItems: "center" }}>
            <Col xs={4}><b>Website</b></Col>
            <Col xs={8}>
              <Form.Control
                type="text"
                value={formData.website}
                onChange={(e) => handleInputChange("website", e.target.value)}
                disabled={!isEditing}
                style={{ width: "200px", backgroundColor: "white" }}
                onClick={handleFieldClick}
              />
            </Col>
          </Row>
        </div>

        {/* Time Zone */}
        <div style={{ padding: "10px", backgroundColor: "#f2f2f2" }}>
          <Row className="mb-3" style={{ alignItems: "center" }}>
            <Col xs={4}><b>Time Zone <span style={{ color: "red" }}>*</span></b></Col>
            <Col xs={8}>
              <Form.Control
                type="text"
                value={formData.time_zone}
                onChange={(e) => handleInputChange("time_zone", e.target.value)}
                disabled={!isEditing}
                style={{ width: "200px", backgroundColor: "white" }}
                onClick={handleFieldClick}
              />
            </Col>
          </Row>
        </div>

        {/* Account Status */}
        <div style={{ padding: "10px", backgroundColor: "#f2f2f2" }}>
          <Row className="mb-3" style={{ alignItems: "center" }}>
            <Col xs={4}><b>Account Status <span style={{ color: "red" }}>*</span></b></Col>
            <Col xs={8}>
              <Form.Control
                type="text"
                value={formData.status}
                onChange={(e) => handleInputChange("status", e.target.value)}
                disabled={!isEditing}
                style={{ width: "200px", backgroundColor: "white" }}
                onClick={handleFieldClick}
              />
            </Col>
          </Row>
        </div>

        {/* DDX Enabled Checkbox */}
        <div style={{ padding: "10px", backgroundColor: "#f2f2f2" }}>
          <Row className="mb-3" style={{ alignItems: "center" }}>
            <Col xs={4}><b>DDX Enabled</b></Col>
            <Col xs={8}>
              <Form.Check
                type="checkbox"
                checked={formData.ddxEnable}
                onChange={(e) => handleInputChange("ddxEnable", e.target.checked)}
                disabled={!isEditing}
                onClick={handleFieldClick}
                aria-label="DDX Enabled"
              />
            </Col>
          </Row>
        </div>

        {/* Account Type */}
        <div style={{ padding: "10px", backgroundColor: "#f2f2f2" }}>
          <Row className="mb-3" style={{ alignItems: "center" }}>
            <Col xs={4}><b>Account Type <span style={{ color: "red" }}>*</span></b></Col>
            <Col xs={8}>
              <Form.Control
                type="text"
                value={formData.account_type}
                onChange={(e) => handleInputChange("account_type", e.target.value)}
                disabled={!isEditing}
                style={{ width: "200px", backgroundColor: "white" }}
                onClick={handleFieldClick}
              />
            </Col>
          </Row>
        </div>

        {/* Quick Code */}
        <div style={{ padding: "10px", backgroundColor: "#f2f2f2" }}>
          <Row className="mb-3" style={{ alignItems: "center" }}>
            <Col xs={4}><b>Quick Code</b></Col>
            <Col xs={8}>
              <Form.Control
                type="text"
                value={formData.quick_code}
                onChange={(e) => handleInputChange("quick_code", e.target.value)}
                disabled={!isEditing}
                style={{ width: "200px", backgroundColor: "white" }}
                placeholder="Enter Quick Code"
                onClick={handleFieldClick}
              />
            </Col>
          </Row>
        </div>

        {/* Deny DDX Case Creation Checkbox */}
        <div style={{ padding: "10px", backgroundColor: "#f2f2f2" }}>
          <Row className="mb-3" style={{ alignItems: "center" }}>
            <Col xs={4}><b>Deny DDX Case Creation</b></Col>
            <Col xs={8}>
              <Form.Check
                type="checkbox"
                checked={formData.deny_ddx_case_creation}
                onChange={(e) => handleInputChange("deny_ddx_case_creation", e.target.checked)}
                disabled={!isEditing}
                onClick={handleFieldClick}
                aria-label="Deny DDX Case Creation"
              />
            </Col>
          </Row>
        </div>

        <br />

        {/* Shipping Address Toggle Header */}
        <div>
          <div
            onClick={() => setShowAddress(!showAddress)}
            style={{
              color: "#004aad",
              padding: "10px",
              cursor: "pointer",
              fontWeight: "bold",
              display: "flex",
              alignItems: "center",
            }}
          >
            <h2><span style={{ marginRight: "3px" }}>Shipping Address</span></h2>
            <span>{showAddress ? "▲" : "▼"}</span>
          </div>
        </div>

        {/* Collapsible Shipping Address Section */}
        <div style={{
          border: "1px solid #ccc",
          padding: "10px",
          marginBottom: "10px",
          borderTop: "3px solid #004aad",
          backgroundColor: "#f2f2f2"
        }}>
          {showAddress && (
            <div style={{ padding: "10px", borderRadius: "5px" }}>
              <Row className="mb-3" style={{ alignItems: "center" }}>
                <Col xs={4}><b>Address</b></Col>
                <Col xs={8}>
                  <Form.Control
                    type="text"
                    value={formData.shipping_address}
                    onChange={(e) => handleInputChange("shipping_address", e.target.value)}
                    disabled={!isEditing}
                    style={{ width: "200px", backgroundColor: "white" }}
                    onClick={handleFieldClick}
                  />
                </Col>
              </Row>

              <Row className="mb-3" style={{ alignItems: "center" }}>
                <Col xs={4}><b>Address 2</b></Col>
                <Col xs={8}>
                  <Form.Control
                    type="text"
                    value={formData.shipping_address2}
                    onChange={(e) => handleInputChange("shipping_address2", e.target.value)}
                    disabled={!isEditing}
                    style={{ width: "200px", backgroundColor: "white" }}
                    onClick={handleFieldClick}
                  />
                </Col>
              </Row>

              <div style={{ padding: "10px", backgroundColor: "#f2f2f2" }}>
                <Row className="mb-3" style={{ alignItems: "center" }}>
                  <Col xs={4}><b>Country <span style={{ color: "red" }}>*</span></b></Col>
                  <Col xs={8}>
                    <Form.Select
                      value={selectedShippingCountryIso}
                      onChange={handleShippingCountryChange}
                      disabled={!isEditing}
                      style={{ width: "200px", backgroundColor: "white" }}
                      onClick={handleFieldClick}
                    >
                      <option value="">Select Country</option>
                      {countries.map((country) => (
                        <option key={country.isoCode} value={country.isoCode}>
                          {country.name}
                        </option>
                      ))}
                    </Form.Select>
                  </Col>
                </Row>
              </div>

              <div style={{ padding: "10px", backgroundColor: "#f2f2f2" }}>
                <Row className="mb-3" style={{ alignItems: "center" }}>
                  <Col xs={4}><b>State <span style={{ color: "red" }}>*</span></b></Col>
                  <Col xs={8}>
                    <Form.Select
                      value={selectedShippingStateIso}
                      onChange={handleShippingStateChange}
                      disabled={!isEditing || !selectedShippingCountryIso}
                      style={{ width: "200px", backgroundColor: "white" }}
                      onClick={handleFieldClick}
                    >
                      <option value="">Select State</option>
                      {shippingStateList.map((state) => (
                        <option key={state.isoCode} value={state.isoCode}>
                          {state.name}
                        </option>
                      ))}
                    </Form.Select>
                  </Col>
                </Row>
              </div>

              <Row className="mb-3" style={{ alignItems: "center" }}>
                <Col xs={4}><b>City</b></Col>
                <Col xs={8}>
                  <Form.Control
                    type="text"
                    value={formData.shipping_city}
                    onChange={(e) => handleInputChange("shipping_city", e.target.value)}
                    disabled={!isEditing}
                    style={{ width: "200px", backgroundColor: "white" }}
                    onClick={handleFieldClick}
                  />
                </Col>
              </Row>

              <Row className="mb-3" style={{ alignItems: "center" }}>
                <Col xs={4}><b>Zip / Postal Code</b></Col>
                <Col xs={8}>
                  <Form.Control
                    type="text"
                    value={formData.shipping_zip}
                    onChange={(e) => handleInputChange("shipping_zip", e.target.value)}
                    disabled={!isEditing}
                    style={{ width: "200px", backgroundColor: "white" }}
                    onClick={handleFieldClick}
                  />
                </Col>
              </Row>

              <div style={{ padding: "10px", backgroundColor: "#f2f2f2" }}>
                <Row className="mb-3" style={{ alignItems: "center" }}>
                  <Col xs={4}><b>Time Zone <span style={{ color: "red" }}>*</span></b></Col>
                  <Col xs={8}>
                    <Form.Control
                      type="text"
                      value={formData.shipping_time_zone}
                      onChange={(e) => handleInputChange("shipping_time_zone", e.target.value)}
                      disabled={!isEditing}
                      style={{ width: "200px", backgroundColor: "white" }}
                      onClick={handleFieldClick}
                    />
                  </Col>
                </Row>
              </div>
            </div>
          )}
        </div>

        {/* Account Hours of Operation Section */}
        <div>
          <h5 style={{
            backgroundColor: "white",
            color: "#004aad",
            padding: "10px 15px",
            fontWeight: "bold",
            marginBottom: "10px"
          }}>
            Account's Hours of Operation
          </h5>
          <div style={{
            backgroundColor: "#f2f2f2",
            padding: "30px",
            borderTop: "3px solid #004aad",
            boxSizing: "border-box",
            margin: "0 auto",
          }}>
            {accountHours.map((item, index) => (
              <p key={index} style={{ margin: "10px 0", fontSize: "14px" }}>
                <b>{item.day}:</b>{" "}
                {isEditing ? (
                  <input
                    type="text"
                    value={item.status}
                    onChange={(e) => handleTimeChange(index, e.target.value)}
                    style={{
                      marginLeft: "10px",
                      padding: "5px",
                      borderRadius: "4px",
                      border: "1px solid #ccc",
                      width: "200px",
                      maxWidth: "100%"
                    }}
                    onClick={handleFieldClick}
                  />
                ) : (
                  item.status
                )}
              </p>
            ))}
            {isEditing ? (
              <Button
                style={{
                  backgroundColor: "#004aad",
                  border: "none",
                  color: "#fff",
                  padding: "8px 16px",
                  marginTop: "15px",
                  borderRadius: "4px"
                }}
                onClick={handleSaveClick}
              >
                Save
              </Button>
            ) : (
              <Button
                style={{
                  backgroundColor: "#004aad",
                  border: "none",
                  color: "#fff",
                  padding: "8px 16px",
                  marginTop: "15px",
                  borderRadius: "4px"
                }}
                onClick={() => setIsEditing(true)}
              >
                <FaPencil /> Edit Hours
              </Button>
            )}
          </div>
        </div>

        {/* Balance Details Section (Static Information) */}
        <div>
          <h5 style={{ color: "#004aad", padding: "5px", fontWeight: "bold" }}>
            Balance Details
          </h5>
          <div
            style={{
              backgroundColor: "#f2f2f2",
              padding: "20px",
              width: "100%",
              borderTop: "3px solid #004aad",
              boxSizing: 'border-box'
            }}
          >
            <p><b>Month to Date:</b> Rs.0.00</p>
            <p><b>1-30 Days:</b> Rs.5,570.00</p>
            <p><b>31-60 Days:</b> Rs.6,784.00</p>
            <p><b>61-90 Days:</b> Rs.1,758.00</p>
            <p><b>91+ Days:</b> Rs.0.00</p>
            <p><b>Amount Overdue:</b> Rs.8,542.00</p>
            <p><b>Amount Due:</b> Rs.14,112.00</p>
          </div>
        </div>

        <br />

        {/* Primary Save Button at Bottom */}
        <div>
          <Button
            style={{
              backgroundColor: '#004aad',
              borderColor: '#004aad',
              marginTop: "10%"
            }}
            onClick={handleSaveClick}
          >
            <FaPencil style={{ marginRight: '6px' }} />
            Save
          </Button>
        </div>
      </div>
    </div>
  );
};

export default Details;